import { useTitle } from "@/hooks/use-title.hook.ts";
import { useEmployeeUpdateAccountSettings } from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useThemeStore } from "@/stores/theme.store.ts";
import { AppThemes, AvailableThemes } from "@/themes/theme.types.ts";
import {
  ButtonIcon,
  FontAwesomeIcon,
  Page,
  Stack,
  Select,
  Text,
  useToasts,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const AccountSettingsTheme = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme, colorMode, setColorMode } = useThemeStore();
  const updateAccountSettings = useEmployeeUpdateAccountSettings();
  const { showInfo } = useToasts();

  useTitle(t("common:theme"));

  const handleThemeChange = (nextTheme: string) => {
    if (nextTheme === theme || updateAccountSettings.isPending) return;

    const previousTheme = theme;
    setTheme(nextTheme);
    updateAccountSettings.mutate(
      { theme: nextTheme },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.employees.accountSettingsEdited);
          showInfo({ title: t("common:changes_saved_successfully") });
        },
        onError: () => setTheme(previousTheme),
      }
    );
  };

  return (
    <Page>
      <Page.Header
        backSlot={
          <ButtonIcon
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-arrow-left"} />}
            onClick={() => navigate("/account-settings")}
          />
        }
        title={t("common:theme")}
      />
      <Page.Content>
        <Stack className={"container-sm mx-auto"} gap={"xl"}>
          <Select
            label={t("common:color_mode")}
            emptyTitle={t("common:select")}
            value={colorMode}
            options={(["light", "dark", "system"] as const).map((mode) => ({
              id: mode,
              title: t(`common:${mode}_mode`),
            }))}
            onChange={(mode) => {
              if (mode === "light" || mode === "dark" || mode === "system") setColorMode(mode);
            }}
          />
          <Stack className={"grid grid-cols-3 gap-(--gap-md) card-panel-sm"}>
            {AvailableThemes.map((availableTheme) => (
              <button
                type={"button"}
                key={availableTheme.id}
                disabled={updateAccountSettings.isPending}
                className={twMerge(
                  "flex w-full border rounded-xs p-3 hover:border-dark/60 cursor-pointer",
                  theme == availableTheme.id && "border-dark/60",
                  updateAccountSettings.isPending && "cursor-wait opacity-60"
                )}
                onClick={() => handleThemeChange(String(availableTheme.id))}
              >
                <Stack className={"w-full"} gap={"md"} items={"center"}>
                  <div
                    style={{
                      background: `conic-gradient(from 180deg at 50% 50%, hsl(${AppThemes[availableTheme.id].appThemeConfig.colorBackgroundGradientFrom}) 0deg, hsl(${AppThemes[availableTheme.id].appThemeConfig.colorBrandDark}) 178.2deg, hsl(${AppThemes[availableTheme.id].appThemeConfig.colorBackgroundGradientTo}) 360deg)`,
                    }}
                    className={"w-full aspect-square rounded-xs"}
                  />
                  <Text className={theme == availableTheme.id ? "text-default" : "text-secondary"}>
                    {availableTheme.title}
                  </Text>
                </Stack>
              </button>
            ))}
          </Stack>
        </Stack>
      </Page.Content>
    </Page>
  );
};
